import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles, CalendarDays } from "lucide-react";
import { BallIcon } from "./Logo";
import { scrollToSection } from "./Navbar";
import api from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1543351611-58f69d7c1781?crop=entropy&cs=srgb&fm=jpg&q=85&auto=format&fit=crop&w=1200";

const titleLines = [
  <>We watch every match.</>,
  <>We debate every goal.</>,
  <>
    So why aren't we{" "}
    <em className="font-serif italic font-medium marker-highlight">playing?</em>
  </>,
];

const HeroSection = () => {
  const [stats, setStats] = useState(null);
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const ballY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    api
      .get("/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats({}));
  }, []);

  const statItems = [
    { value: stats?.responses, label: "survey answers" },
    { value: stats?.news, label: "headlines posted" },
    { value: stats?.teams, label: "teams on the table" },
  ];

  return (
    <section ref={sectionRef} id="top" className="relative overflow-hidden pitch-net">
      <div className="absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-sage/60 blur-3xl dark:bg-turf/10" aria-hidden="true" />
      <div className="absolute top-56 -left-40 h-96 w-96 rounded-full bg-flame-soft blur-3xl dark:bg-flame/10" aria-hidden="true" />
      <svg
        className="absolute top-16 left-1/2 -translate-x-1/2 opacity-[0.5] dark:opacity-[0.35] pointer-events-none"
        width="640"
        height="640"
        viewBox="0 0 640 640"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="320" cy="320" r="280" stroke="rgba(30,77,43,0.14)" strokeWidth="1.5" strokeDasharray="3 10" />
        <circle cx="320" cy="320" r="160" stroke="rgba(30,77,43,0.10)" strokeWidth="1.5" />
        <circle cx="320" cy="320" r="8" fill="rgba(30,77,43,0.18)" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-12 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span
              data-testid="hero-stats-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-card/90 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-pitch dark:text-turf shadow-soft"
            >
              <Sparkles className="h-3.5 w-3.5 text-flame" /> A research project by a 15-year-old
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="mt-6 font-display text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-7xl font-bold tracking-tighter"
          >
            {titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Sidelines is my attempt to find out why so many in Morbi play football —
            and why so many more only watch. Take the survey, follow the games, and
            tell me your side of the story.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-survey"
              onClick={() => scrollToSection("survey")}
              className="inline-flex items-center gap-2 h-13 h-[3.25rem] px-8 rounded-full bg-pitch text-cream font-semibold shadow-soft hover:bg-pitch-deep hover:-translate-y-0.5 hover:shadow-lift transition-all dark:bg-turf dark:text-background"
            >
              Take the 2-minute survey <ArrowDown className="h-4 w-4" />
            </button>
            <Link
              to="/big-games"
              data-testid="hero-cta-games"
              className="inline-flex items-center gap-2 h-[3.25rem] px-8 rounded-full border-2 border-pitch/25 dark:border-turf/40 font-semibold text-pitch dark:text-turf hover:border-pitch dark:hover:border-turf hover:-translate-y-0.5 hover:bg-card transition-all"
            >
              Fixtures, tables & stats <CalendarDays className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div
            data-testid="hero-stats-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {statItems.map((s) => (
              <div key={s.label} className="rounded-2xl bg-card/90 border border-foreground/10 px-5 py-3 shadow-soft">
                <div className="font-display text-2xl font-bold text-pitch dark:text-turf" data-testid="hero-stat-value">
                  {s.value ?? "—"}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: imgY }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: 4 }}
              animate={{ opacity: 1, scale: 1, rotate: 2 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[2.25rem] border-4 border-card shadow-lift"
            >
              <img
                src={HERO_IMG}
                alt="A football resting on the halfway line of a local pitch"
                className="w-full h-[360px] sm:h-[420px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pitch/40 via-transparent to-transparent" aria-hidden="true" />
            </motion.div>

            <motion.div style={{ y: ballY }} className="absolute -top-7 -right-3 sm:-right-6">
              <div className="animate-float-y h-20 w-20 rounded-full bg-card shadow-lift border border-foreground/10 grid place-items-center sticker-tilt">
                <BallIcon className="h-12 w-12 text-pitch dark:text-turf" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="absolute -bottom-6 -left-3 sm:-left-6 sticker-tilt rounded-2xl bg-flame text-white font-display font-bold px-5 py-3 shadow-lift"
            >
              est. this term
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-90 mt-0.5">
                watch · debate · play?
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="relative pb-8 flex justify-center" aria-hidden="true">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown className="h-5 w-5 text-muted-foreground" />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
